// src/store/slices/authSlice.ts
import type { RootState } from ".."
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit"
import axiosInstance, { setAuthHeader } from "api/service"
import { isTelegramEnv } from "utils/tgWebApp"
import { getRefCode, clearRefCode } from "utils/refCode"

export type AuthProvider = "telegram" | "web" | "email" | "google" | "yandex"
export type AuthStatus =
  | "idle"
  | "authenticating"
  | "authenticated"
  | "unauthenticated"
  | "error"

interface AuthState {
  status: AuthStatus
  provider: AuthProvider | null
  token: string | null
  error: string | null
}

const initialState: AuthState = {
  status: "idle",
  provider: null,
  token: null,
  error: null
}

const ONE_DAY_MS = 24 * 60 * 60 * 1000

const persistToken = (token: string) => {
  localStorage.setItem("accessToken", token)
  localStorage.setItem("tokenReceiveTime", String(Date.now()))
  setAuthHeader(token)
  clearRefCode()
}

// ── Thunks ──────────────────────────────────────────────────────────────────

export const restoreSession = createAsyncThunk<
  string,
  void,
  { rejectValue: string }
>("auth/restoreSession", async (_, { rejectWithValue }) => {
  const token = localStorage.getItem("accessToken")
  const tokenReceiveTime = localStorage.getItem("tokenReceiveTime")

  if (!token || !tokenReceiveTime) {
    return rejectWithValue("no_token")
  }

  const isValid = Date.now() - Number(tokenReceiveTime) < ONE_DAY_MS
  if (!isValid) {
    localStorage.removeItem("accessToken")
    localStorage.removeItem("tokenReceiveTime")
    return rejectWithValue("token_expired")
  }

  setAuthHeader(token)
  return token
})

export const loginWithTelegram = createAsyncThunk<
  string,
  void,
  { rejectValue: string }
>("auth/loginWithTelegram", async (_, { rejectWithValue }) => {
  if (!isTelegramEnv()) return rejectWithValue("not_telegram_env")

  const currentTgId = window.Telegram.WebApp.initDataUnsafe.user.id.toString()
  const storedTgId = localStorage.getItem("tgUserId")

  if (storedTgId && storedTgId !== currentTgId) {
    localStorage.removeItem("accessToken")
    localStorage.removeItem("tokenReceiveTime")
  }

  try {
    const refCode = getRefCode()
    const authUrl = refCode ? `/auth/telegram?ref=${refCode}` : "/auth/telegram"

    const { data } = await axiosInstance.post(authUrl, {
      initData: window.Telegram.WebApp.initData,
      id: currentTgId,
      photo_url:
        window.Telegram.WebApp.initDataUnsafe.user.photo_url?.toString(),
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    })

    if (!data.accessToken) return rejectWithValue("telegram_auth_failed")

    persistToken(data.accessToken)
    localStorage.setItem("tgUserId", currentTgId)

    return data.accessToken
  } catch (e: any) {
    return rejectWithValue(e?.response?.data?.message ?? "telegram_auth_failed")
  }
})

export const loginWithEmailOtp = createAsyncThunk<
  string,
  { email: string; code: string },
  { rejectValue: string }
>("auth/loginWithEmailOtp", async ({ email, code }, { rejectWithValue }) => {
  try {
    const { data } = await axiosInstance.post("/auth/email/verify-code", {
      email,
      code
    })

    if (!data.accessToken) return rejectWithValue("email_otp_auth_failed")

    persistToken(data.accessToken)

    return data.accessToken
  } catch (e: any) {
    return rejectWithValue(e?.response?.data?.error ?? "email_otp_auth_failed")
  }
})

export const sendEmailOtpCode = createAsyncThunk<
  void,
  { email: string },
  { rejectValue: string }
>("auth/sendEmailOtpCode", async ({ email }, { rejectWithValue }) => {
  try {
    const refCode = getRefCode()
    await axiosInstance.post("/auth/email/send-code", { email, refCode })
  } catch (e: any) {
    return rejectWithValue(e?.response?.data?.error ?? "email_send_failed")
  }
})

export interface TelegramWebLoginData {
  id: number
  first_name: string
  last_name?: string
  username?: string
  photo_url?: string
  auth_date: number
  hash: string
}

export const loginWithWeb = createAsyncThunk<
  string,
  TelegramWebLoginData,
  { rejectValue: string }
>("auth/loginWithWeb", async (webData, { rejectWithValue }) => {
  try {
    const refCode = getRefCode()
    const authUrl = refCode ? `/auth/web?ref=${refCode}` : "/auth/web"

    const { data } = await axiosInstance.post(authUrl, {
      ...webData,
      id: String(webData.id),
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    })

    if (!data.accessToken) return rejectWithValue("web_auth_failed")

    persistToken(data.accessToken)

    return data.accessToken
  } catch (e: any) {
    return rejectWithValue(e?.response?.data?.message ?? "web_auth_failed")
  }
})

export const loginWithOAuthToken = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>("auth/loginWithOAuthToken", async (token) => {
  persistToken(token)
  return token
})

// ── Slice ────────────────────────────────────────────────────────────────────

export const AuthSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setStatus: (state, { payload }: PayloadAction<AuthStatus>) => {
      state.status = payload
    },
    logout: (state) => {
      state.status = "unauthenticated"
      state.token = null
      state.provider = null
      state.error = null
      localStorage.removeItem("accessToken")
      localStorage.removeItem("tokenReceiveTime")
    }
  },
  extraReducers: (builder) => {
    // pending
    const allLoginThunks = [
      restoreSession,
      loginWithTelegram,
      loginWithWeb,
      loginWithEmailOtp,
      loginWithOAuthToken
    ]
    allLoginThunks.forEach((thunk) => {
      builder.addCase(thunk.pending, (state) => {
        state.status = "authenticating"
        state.error = null
      })
    })

    // fulfilled
    builder
      .addCase(restoreSession.fulfilled, (state, action) => {
        state.status = "authenticated"
        state.provider = null // provider unknown on restore
        state.token = action.payload
        state.error = null
      })
      .addCase(loginWithTelegram.fulfilled, (state, action) => {
        state.status = "authenticated"
        state.provider = "telegram"
        state.token = action.payload
        state.error = null
      })
      .addCase(loginWithEmailOtp.fulfilled, (state, action) => {
        state.status = "authenticated"
        state.provider = "email"
        state.token = action.payload
        state.error = null
      })
      .addCase(loginWithWeb.fulfilled, (state, action) => {
        state.status = "authenticated"
        state.provider = "web"
        state.token = action.payload
        state.error = null
      })
      .addCase(loginWithOAuthToken.fulfilled, (state, action) => {
        state.status = "authenticated"
        state.provider = null
        state.token = action.payload
        state.error = null
      })

    // rejected
    builder
      .addCase(restoreSession.rejected, (state) => {
        // Normal: no token or expired — user just needs to login
        state.status = "unauthenticated"
        state.error = null // не показуємо помилку для "немає токена"
        state.token = null
      })
      .addCase(loginWithTelegram.rejected, (state, action) => {
        state.status = "error"
        state.error = action.payload ?? "telegram_auth_failed"
        state.token = null
      })
      .addCase(loginWithEmailOtp.rejected, (state, action) => {
        state.status = "error"
        state.error = action.payload ?? "auth_failed"
        state.token = null
      })
      .addCase(loginWithWeb.rejected, (state, action) => {
        state.status = "error"
        state.error = action.payload ?? "web_auth_failed"
        state.token = null
      })
      .addCase(loginWithOAuthToken.rejected, (state, action) => {
        state.status = "error"
        state.error = action.payload ?? "oauth_auth_failed"
        state.token = null
      })
  }
})

// ── Selectors ────────────────────────────────────────────────────────────────

const selectStatus = (state: RootState) => state.auth.status
const selectToken = (state: RootState) => state.auth.token
const selectProvider = (state: RootState) => state.auth.provider
const selectError = (state: RootState) => state.auth.error

export const AuthSelectors = {
  selectStatus,
  selectToken,
  selectProvider,
  selectError
}

export const { setStatus, logout } = AuthSlice.actions
